// src/data/m2/Unit8.js

export const M2_UNIT_8 = {
  id: "m2-u8",
  title: "Unit 8: Real Talk Connectors",
  topic: "Conjunctions (And / But) & Real Slang",
  iconType: "star", 
  grammarImage: "/m2-u8-grammar.jpg", 
  grammarText: "No seas un robot. Usa **'And'** para fluir y **'But'** para sorprender o corregir. Aprende también modismos como *'Pig out'* y *'Broke'*.",
  
  sets: [
    // ----------------------------------------------------------------------
    // SET 1: BASIC CONNECTIONS (And vs But)
    // Objetivo: Distinguir suma vs contraste.
    // ----------------------------------------------------------------------
    {
      id: "u24-set1",
      title: "Set 1: Basic Connections",
      questions: [
        { 
          type: "mc", 
          q: "I'm tired ___ hungry.", 
          options: ["and", "but"], 
          a: "and",
          tip: "Dos sensaciones negativas se suman -> And."
        },
        { 
          type: "mc", 
          q: "I like pizza, ___ I don't eat it every day.", 
          options: ["but", "and"], 
          a: "but",
          tip: "Contraste positivo/negativo -> But."
        },
        { 
          type: "match", 
          q: "Conecta la lógica:",
          pairs: [
            { left: "Rich (+) ... Sad (-)", right: "But" },
            { left: "Rich (+) ... Happy (+)", right: "And" },
            { left: "Tired (-) ... Hungry (-)", right: "And" },
            { left: "Cheap (+) ... Bad (-)", right: "But" }
          ],
          a: "completed"
        },
        { 
          type: "order", 
          q: "Ordena: Estoy cansado pero feliz.", 
          words: ["I", "am", "tired,", "but", "happy."], 
          a: "I am tired, but happy." 
        },
        { 
          type: "blank", 
          q: "She's smart ___ funny. (Both +)", 
          options: ["and"], 
          a: "and" 
        },
        { 
          type: "mc", 
          q: "It's an old car, ___ it's fast.", 
          options: ["but", "and"], 
          a: "but" 
        },
        { 
          type: "input", 
          q: "Escribe el conector de contraste (pero):", 
          a: "but" 
        },
        { 
          type: "order", 
          q: "Ordena: Ella es agradable y amable.", 
          words: ["She", "is", "nice", "and", "kind."], 
          a: "She is nice and kind." 
        },
        { 
          type: "mc", 
          q: "I want to go, ___ I have no money.", 
          options: ["but", "and"], 
          a: "but" 
        },
        { 
          type: "blank", 
          q: "He is tall ___ handsome.", 
          options: ["and"], 
          a: "and" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 2: WEEKEND VIBES (Introduction to Slang)
    // Objetivo: Pig Out, Chill Out, Hang Out.
    // ----------------------------------------------------------------------
    {
      id: "u24-set2",
      title: "Set 2: Weekend Vibes",
      questions: [
        { 
          type: "mc", 
          q: "I like to eat pizza, ___ I mean really pig out! 🍕", 
          options: ["and", "but"], 
          a: "and",
          successTip: "¡Pro Tip! 'Pig out' significa comer muchísimo (como cerdito)."
        },
        { 
          type: "blank", 
          q: "On Sundays I relax ___ just chill out.", 
          options: ["and"], 
          a: "and",
          tip: "'Chill out' es relajarse totalmente. Se suma a relax -> And."
        },
        { 
          type: "match", 
          q: "Slang Match:",
          pairs: [
            { left: "Pig out", right: "Eat a lot 🐷" },
            { left: "Chill out", right: "Relax 😎" },
            { left: "Hang out", right: "Spend time with friends 👯" },
            { left: "Go out", right: "Leave home 🏠" }
          ],
          a: "completed"
        },
        { 
          type: "order", 
          q: "Ordena: Vamos a relajarnos y comer mucho.", 
          words: ["Let's", "chill", "out", "and", "pig", "out."], 
          a: "Let's chill out and pig out." 
        },
        { 
          type: "mc", 
          q: "I want to go out, ___ I'm broke. 💸", 
          options: ["but", "and"], 
          a: "but",
          successTip: "Si estás 'Broke', no tienes dinero (estás quebrado)."
        },
        { 
          type: "input", 
          q: "Escribe la palabra para 'Estar quebrado' ($0):", 
          a: "broke" 
        },
        { 
          type: "mc", 
          q: "Do you want to come over ___ hang out?", 
          options: ["and", "but"], 
          a: "and" 
        },
        { 
          type: "blank", 
          q: "I have money, ___ I'm tired. (Contrast)", 
          options: ["but"], 
          a: "but" 
        },
        { 
          type: "order", 
          q: "Ordena: Estoy quebrado pero feliz.", 
          words: ["I", "am", "broke,", "but", "happy."], 
          a: "I am broke, but happy." 
        },
        { 
          type: "match", 
          q: "Conecta la acción:",
          pairs: [
            { left: "Netflix", right: "And chill" },
            { left: "Pizza", right: "And pig out" },
            { left: "Friends", right: "And hang out" }
          ],
          a: "completed"
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 3: POLITENESS & CORRECTIONS (But actually...)
    // Objetivo: Corregir o suavizar mensajes.
    // ----------------------------------------------------------------------
    {
      id: "u24-set3",
      title: "Set 3: 'But actually...'",
      questions: [
        { 
          type: "mc", 
          q: "It's expensive, ___ it's worth it. (Vale la pena)", 
          options: ["but", "and"], 
          a: "but",
          successTip: "'Worth it' significa que el valor justifica el precio."
        },
        { 
          type: "blank", 
          q: "He looks mean (malo), ___ he's actually sweet.", 
          options: ["but"], 
          a: "but",
          tip: "'Actually' se usa mucho con 'But' para revelar la verdad."
        },
        { 
          type: "order", 
          q: "Ordena: Es barato, pero bueno.", 
          words: ["It's", "cheap,", "but", "good."], 
          a: "It's cheap, but good." 
        },
        { 
          type: "mc", 
          q: "Sorry, ___ I can't help you.", 
          options: ["but", "and"], 
          a: "but",
          tip: "Usamos 'Sorry, but...' para dar malas noticias suavemente."
        },
        { 
          type: "match", 
          q: "Conecta la frase:",
          pairs: [
            { left: "It's hard", right: "But it's worth it." },
            { left: "I'm sorry", right: "But I'm busy." },
            { left: "He's weird", right: "But cool." },
            { left: "She's strict", right: "But fair." }
          ],
          a: "completed"
        },
        { 
          type: "input", 
          q: "Escribe la palabra para 'En realidad' (A...):", 
          a: "actually" 
        },
        { 
          type: "mc", 
          q: "It's ugly, ___ comfortable.", 
          options: ["but", "and"], 
          a: "but" 
        },
        { 
          type: "blank", 
          q: "I like him, ___ not his brother.", 
          options: ["but"], 
          a: "but" 
        },
        { 
          type: "order", 
          q: "Ordena: Lo siento, pero no.", 
          words: ["I", "am", "sorry,", "but", "no."], 
          a: "I am sorry, but no." 
        },
        { 
          type: "mc", 
          q: "It's raining, ___ we are going out.", 
          options: ["but", "and"], 
          a: "but" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 4: LISTS WITH ATTITUDE (Oxford Comma)
    // Objetivo: Listas largas y énfasis.
    // ----------------------------------------------------------------------
    {
      id: "u24-set4",
      title: "Set 4: Lists with Attitude",
      questions: [
        { 
          type: "mc", 
          q: "I need coffee, energy, ___ a miracle! ☕", 
          options: ["and", "but"], 
          a: "and",
          tip: "La 'Oxford Comma' es la coma antes del último 'and'."
        },
        { 
          type: "blank", 
          q: "He's tall, dark, ___ handsome.", 
          options: ["and"], 
          a: "and",
          successTip: "Frase hecha muy común: 'Tall, dark, and handsome'."
        },
        { 
          type: "order", 
          q: "Ordena: Necesito amor y pizza.", 
          words: ["I", "need", "love", "and", "pizza."], 
          a: "I need love and pizza." 
        },
        { 
          type: "mc", 
          q: "I can sing, dance, ___ cook.", 
          options: ["and", "but"], 
          a: "and" 
        },
        { 
          type: "input", 
          q: "Escribe la palabra faltante: 'Blood, sweat ___ tears.'", 
          a: "and" 
        },
        { 
          type: "match", 
          q: "Completa la lista:",
          pairs: [
            { left: "Breakfast, Lunch", right: "And Dinner" },
            { left: "Red, White", right: "And Blue" },
            { left: "Me, Myself", right: "And I" },
            { left: "Rock", right: "And Roll" }
          ],
          a: "completed"
        },
        { 
          type: "mc", 
          q: "We have wine, beer, ___ water.", 
          options: ["and", "but"], 
          a: "and" 
        },
        { 
          type: "blank", 
          q: "I want a car, a house, ___ a boat.", 
          options: ["and"], 
          a: "and" 
        },
        { 
          type: "order", 
          q: "Ordena: Ella es inteligente y divertida.", 
          words: ["She", "is", "smart", "and", "funny."], 
          a: "She is smart and funny." 
        },
        { 
          type: "mc", 
          q: "It's fast, easy, ___ free.", 
          options: ["and", "but"], 
          a: "and" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 5: MAKING EXCUSES (I want to, but...)
    // Objetivo: Rechazar planes como un nativo.
    // ----------------------------------------------------------------------
    {
      id: "u24-set5",
      title: "Set 5: Making Excuses",
      questions: [
        { 
          type: "mc", 
          q: "I'd love to go, ___ I'm swamped. (Ocupadísimo)", 
          options: ["but", "and"], 
          a: "but",
          successTip: "'Swamped' (inundado) significa tener muchísimo trabajo."
        },
        { 
          type: "match", 
          q: "Conecta la excusa:",
          pairs: [
            { left: "I want to go", right: "But I'm broke 💸" },
            { left: "I'd love to", right: "But I'm tired 😴" },
            { left: "Sounds fun", right: "But I have work 💼" },
            { left: "Maybe later", right: "But not now ⏳" }
          ],
          a: "completed" 
        },
        { 
          type: "blank", 
          q: "It sounds great, ___ I have a date.", 
          options: ["but"], 
          a: "but" 
        },
        { 
          type: "order", 
          q: "Ordena: Me encantaría, pero no puedo.", 
          words: ["I'd", "love", "to,", "but", "I", "can't."], 
          a: "I'd love to, but I can't." 
        },
        { 
          type: "mc", 
          q: "I'm free today, ___ busy tomorrow.", 
          options: ["but", "and"], 
          a: "but" 
        },
        { 
          type: "input", 
          q: "Escribe: Ocupado (en inglés)", 
          a: "busy" 
        },
        { 
          type: "mc", 
          q: "Can you help? I would, ___ I am swamped.", 
          options: ["but", "and"], 
          a: "but" 
        },
        { 
          type: "blank", 
          q: "I like you, ___ I have a boyfriend.", 
          options: ["but"], 
          a: "but" 
        },
        { 
          type: "order", 
          q: "Ordena: Suena bien, pero estoy quebrado.", 
          words: ["Sounds", "good,", "but", "I'm", "broke."], 
          a: "Sounds good, but I'm broke." 
        },
        { 
          type: "mc", 
          q: "I want the car, ___ it's too expensive.", 
          options: ["but", "and"], 
          a: "but" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 6: SLANG CHALLENGE
    // Objetivo: Vocabulario informal.
    // ----------------------------------------------------------------------
    {
      id: "u24-set6",
      title: "Set 6: Slang Challenge",
      questions: [
        { 
          type: "mc", 
          q: "Which means 'Relax'?", 
          options: ["Chill out", "Pig out", "Break out"], 
          a: "Chill out" 
        },
        { 
          type: "mc", 
          q: "Which means 'No money'?", 
          options: ["Broke", "Broken", "Rich"], 
          a: "Broke" 
        },
        { 
          type: "blank", 
          q: "Let's order pizza ___ pig out!", 
          options: ["and"], 
          a: "and" 
        },
        { 
          type: "input", 
          q: "Escribe la palabra para 'Estar quebrado' ($0):", 
          a: "broke" 
        },
        { 
          type: "match", 
          q: "Opposites:",
          pairs: [
            { left: "Broke", right: "Rich" },
            { left: "Stressed", right: "Chill" },
            { left: "Hungry", right: "Full" },
            { left: "Pig out", right: "Diet" }
          ],
          a: "completed" 
        },
        { 
          type: "mc", 
          q: "I'm not hungry, ___ I will eat.", 
          options: ["but", "and"], 
          a: "but" 
        },
        { 
          type: "order", 
          q: "Ordena: Vamos a pasar el rato.", 
          words: ["Let's", "hang", "out."], 
          a: "Let's hang out." 
        },
        { 
          type: "mc", 
          q: "Are you busy? No, just ___ out.", 
          options: ["chilling", "pigging"], 
          a: "chilling" 
        },
        { 
          type: "blank", 
          q: "I love burgers ___ fries. (Pig out)", 
          options: ["and"], 
          a: "and" 
        },
        { 
          type: "input", 
          q: "Escribe: 'Comer mucho' (Slang - 2 palabras)", 
          a: "pig out" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 7: READING CONTEXT (A Bad Date)
    // Objetivo: Comprensión de lectura divertida.
    // ----------------------------------------------------------------------
    {
      id: "u24-set7",
      title: "Set 7: A Bad Date",
      questions: [
        { 
          type: "mc", 
          q: "TEXT: 'He was handsome ___ boring.'", 
          options: ["but", "and"], 
          a: "but" 
        },
        { 
          type: "mc", 
          q: "TEXT: 'He talked ___ talked ___ talked.'", 
          options: ["and / and", "but / but"], 
          a: "and / and",
          tip: "Repetir 'and' enfatiza que no paraba de hablar."
        },
        { 
          type: "blank", 
          q: "TEXT: 'I wanted to leave, ___ I was polite.'", 
          options: ["but"], 
          a: "but" 
        },
        { 
          type: "mc", 
          q: "TEXT: 'He ordered lobster (langosta) ___ he was broke!'", 
          options: ["but", "and"], 
          a: "but",
          successTip: "¡El colmo! Pidió caro pero no tenía dinero."
        },
        { 
          type: "order", 
          q: "Ordena: Él era guapo pero aburrido.", 
          words: ["He", "was", "handsome,", "but", "boring."], 
          a: "He was handsome, but boring." 
        },
        { 
          type: "mc", 
          q: "The food was good, ___ the date was bad.", 
          options: ["but", "and"], 
          a: "but" 
        },
        { 
          type: "blank", 
          q: "He forgot his wallet ___ his phone.", 
          options: ["and"], 
          a: "and" 
        },
        { 
          type: "match", 
          q: "Resumen de la cita:",
          pairs: [
            { left: "Boy", right: "Handsome but boring" },
            { left: "Food", right: "Good but expensive" },
            { left: "Girl", right: "Polite but annoyed" },
            { left: "Result", right: "No second date" }
          ],
          a: "completed"
        },
        { 
          type: "input", 
          q: "Escribe: Aburrido (en inglés)", 
          a: "boring" 
        },
        { 
          type: "mc", 
          q: "Will she call him? No, ___ she will block him.", 
          options: ["but", "and"], 
          a: "and" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 8: WORK & LIFE BALANCE
    // Objetivo: Hablar de rutinas y estrés (Contexto Laboral).
    // ----------------------------------------------------------------------
    {
      id: "u24-set8",
      title: "Set 8: Work & Stress",
      questions: [
        { 
          type: "mc", 
          q: "I like my job, ___ it's very stressful.", 
          options: ["but", "and"], 
          a: "but" 
        },
        { 
          type: "blank", 
          q: "I work hard ___ I play hard.", 
          options: ["and"], 
          a: "and",
          successTip: "Frase común: 'Work hard, play hard'."
        },
        { 
          type: "order", 
          q: "Ordena: Estoy cansado, pero necesito trabajar.", 
          words: ["I", "am", "tired,", "but", "I", "need", "to", "work."], 
          a: "I am tired, but I need to work." 
        },
        { 
          type: "mc", 
          q: "I have a meeting at 9 ___ another at 10.", 
          options: ["and", "but"], 
          a: "and" 
        },
        { 
          type: "input", 
          q: "Escribe: Estresante (en inglés)", 
          a: "stressful" 
        },
        { 
          type: "match", 
          q: "Sentimientos laborales:",
          pairs: [
            { left: "Swamped", right: "Too busy" },
            { left: "Bored", right: "Nothing to do" },
            { left: "Excited", right: "Happy about work" }
          ],
          a: "completed"
        },
        { 
          type: "mc", 
          q: "He is rich, ___ he is unhappy.", 
          options: ["but", "and"], 
          a: "but" 
        },
        { 
          type: "blank", 
          q: "I need a vacation ___ a raise (aumento).", 
          options: ["and"], 
          a: "and" 
        },
        { 
          type: "order", 
          q: "Ordena: Ella es la jefa y es estricta.", 
          words: ["She", "is", "the", "boss", "and", "she", "is", "strict."], 
          a: "She is the boss and she is strict." 
        },
        { 
          type: "mc", 
          q: "I'm swamped, ___ I love it.", 
          options: ["but", "and"], 
          a: "but" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 9: ERROR CORRECTION (Grammar Police)
    // Objetivo: Detectar errores comunes de conexión.
    // ----------------------------------------------------------------------
    {
      id: "u24-set9",
      title: "Set 9: Find the Mistake",
      questions: [
        { 
          type: "mc", 
          q: "Error: 'I like pizza but I eat it every day.'", 
          options: ["Fix: AND I eat it...", "Fix: BUT I don't eat it..."], 
          a: "Fix: AND I eat it...",
          tip: "Si te gusta y la comes, es una suma (+) -> And."
        },
        { 
          type: "mc", 
          q: "Error: 'He is rich and unhappy.' (Usually contrast)", 
          options: ["Fix: BUT unhappy", "Fix: AND happy"], 
          a: "Fix: BUT unhappy",
          tip: "Rico (+) pero infeliz (-) es un contraste -> But."
        },
        { 
          type: "input", 
          q: "Corrige: 'I am tired but I sleep.' (Causa-Efecto)", 
          a: "I am tired and I sleep" // O "so I sleep" pero aquí practicamos AND
        },
        { 
          type: "order", 
          q: "Corrige: She nice but strict.", 
          words: ["She", "is", "nice,", "but", "strict."], 
          a: "She is nice, but strict." 
        },
        { 
          type: "match", 
          q: "Conector correcto:",
          pairs: [
            { left: "Hot ... Cold", right: "But" },
            { left: "Hot ... Sunny", right: "And" },
            { left: "Cheap ... Good", right: "But (Surprise)" }
          ],
          a: "completed"
        },
        { 
          type: "mc", 
          q: "Error: 'I have a car but a bike.'", 
          options: ["Fix: and a bike", "Fix: or a bike"], 
          a: "Fix: and a bike"
        },
        { 
          type: "blank", 
          q: "I study hard, ___ I fail. (Unexpected)", 
          options: ["but"], 
          a: "but" 
        },
        { 
          type: "order", 
          q: "Corrige: He smart but lazy.", 
          words: ["He", "is", "smart,", "but", "lazy."], 
          a: "He is smart, but lazy." 
        },
        { 
          type: "mc", 
          q: "Error: 'I'm broke and I buy a Ferrari.'", 
          options: ["Impossible context", "Correct"], 
          a: "Impossible context",
          successTip: "Si estás 'Broke', no puedes comprar un Ferrari."
        },
        { 
          type: "input", 
          q: "Traduce: 'Pero' (en inglés)", 
          a: "but" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 10: MASTER CHALLENGE (Ultimate Mix)
    // Objetivo: Mezcla total de Slang, Gramática y Contexto.
    // ----------------------------------------------------------------------
    {
      id: "u24-set10",
      title: "Set 10: The Master Connector",
      questions: [
        { 
          type: "order", 
          q: "Crea: Quiero relajarme, pero estoy inundado (de trabajo).", 
          words: ["I", "want", "to", "chill", "out,", "but", "I'm", "swamped."], 
          a: "I want to chill out, but I'm swamped." 
        },
        { 
          type: "input", 
          q: "Traduce: 'Estoy quebrado' (slang): I'm ___.", 
          a: "broke" 
        },
        { 
          type: "mc", 
          q: "Complete: 'Let's hang out ___ watch a movie.'", 
          options: ["and", "but"], 
          a: "and" 
        },
        { 
          type: "blank", 
          q: "It's cheap, ___ it tastes bad.", 
          options: ["but"], 
          a: "but" 
        },
        { 
          type: "match", 
          q: "Ultimate Match:",
          pairs: [
            { left: "Pig out", right: "Pizza & Burgers" },
            { left: "Broke", right: "Empty Wallet" },
            { left: "Swamped", right: "Too much work" },
            { left: "Chill", right: "Relaxed" }
          ],
          a: "completed" 
        },
        { 
          type: "mc", 
          q: "I study hard ___ I play hard.", 
          options: ["and", "but"], 
          a: "and" 
        },
        { 
          type: "order", 
          q: "Ordena: Tengo hambre y sed.", 
          words: ["I", "am", "hungry", "and", "thirsty."], 
          a: "I am hungry and thirsty." 
        },
        { 
          type: "blank", 
          q: "She is nice ___ she talks too much.", 
          options: ["but"], 
          a: "but" 
        },
        { 
          type: "mc", 
          q: "Are you ready? Yes ___ no.", 
          options: ["and", "but"], 
          a: "and" 
        },
        { 
          type: "input", 
          q: "Escribe: 'Inundado de trabajo' (Slang)", 
          a: "swamped" 
        }
      ]
    }
  ]
};